import { css, type Interpolation, type Theme } from "@emotion/react";
import { ChevronDown } from "lucide-react";
import { enqueueSnackbar } from "notistack";
import {
	type FocusEventHandler,
	isValidElement,
	type KeyboardEventHandler,
	useEffect,
	useId,
	useState,
} from "react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";

export type FwNumberDropDownProps = {
	value: number;
	onChange?: (newValue: number, oldvalue: number) => void;
	label?: string;
	required?: boolean;
	children?: React.ReactNode[];
	maxQty?: number;
	maxDropDown?: number;
	labelPrefix?: string;
	inputHeight?: string;
	css?: Interpolation<Theme>;
};
export const FwNumberDropDown: React.FC<FwNumberDropDownProps> = (props) => {
	let maxDropDown = props.maxDropDown ?? 9;
	if (props.maxQty !== undefined && maxDropDown > props.maxQty) {
		maxDropDown = props.maxQty;
	}
	const value = props.value.toString();
	const [showTextbox, setShowTextbox] = useState<boolean>(
		props.value > maxDropDown,
	);
	const [textboxValue, setTextboxValue] = useState<string>(value);
	const children: React.ReactNode[] = props.children ?? [];
	const showTextboxValue = "SHOWTEXTBOX";
	const baseId = useId();
	const fieldId = `${baseId}-field`;
	const labelPrefix = props.labelPrefix ? `${props.labelPrefix}: ` : "";
	const inputHeight = props.inputHeight ? props.inputHeight : "32px";
	const translate = useTranslate();

	useEffect(() => {
		if (!showTextbox) {
			setTextboxValue(props.value.toString());
		}
	}, [props.value, showTextbox]);

	const options: { value: string; label: React.ReactNode }[] = [];

	if (children.length === 0) {
		for (let i = 0; i <= maxDropDown; i++) {
			const val = i.toString();
			options.push({ value: val, label: val });
		}
		if (props.maxQty === undefined || maxDropDown < props.maxQty) {
			options.push({
				value: showTextboxValue,
				label: `${maxDropDown + 1}+`,
			});
		}
	} else {
		for (const child of children) {
			if (
				!isValidElement<{ value?: unknown; children?: React.ReactNode }>(child)
			) {
				continue;
			}

			const childValue = child.props?.value;
			if (childValue === undefined || childValue === null) {
				continue;
			}

			options.push({
				value: String(childValue),
				label: child.props?.children ?? String(childValue),
			});
		}
	}

	const onChangeSelect = async (newValue: string) => {
		return Promise.resolve().then(() => {
			const newShowTextbox = newValue === showTextboxValue;
			if (showTextbox !== newShowTextbox) {
				setShowTextbox(newShowTextbox);
			}
			if (newValue !== showTextboxValue) {
				if (props.onChange) {
					props.onChange(+newValue, props.value);
				}
			}
		});
	};

	const validateTextboxValue = (value: string) => {
		let newValue = parseFloat(value);
		const maxQty = props.maxQty;

		// first make sure we have a valid value for the currentValue in case we need to reset the newValue
		let currentValue = props.value;
		if (Number.isNaN(currentValue)) {
			currentValue = 0;
		}
		if (maxQty !== undefined) {
			if (currentValue > (maxQty ?? 0)) {
				currentValue = maxQty ?? 0;
			}
		}
		if (currentValue < 0) {
			currentValue = 0;
		}

		//console.log({
		//    newValue,
		//    maxQty,
		//    currentValue
		//})

		// next make sure the newValue is valid, if not reset to the currentValue
		if (Number.isNaN(newValue)) {
			if (Number.isNaN(currentValue)) {
				newValue = 0;
			} else {
				newValue = currentValue;
			}
			enqueueSnackbar<"error">(translate("Invalid quantity"));
		}
		if (maxQty !== undefined) {
			if (newValue > maxQty) {
				newValue = maxQty;
				enqueueSnackbar<"error">(translate("Invalid quantity"));
			}
		}
		if (newValue < 0) {
			newValue = 0;
			enqueueSnackbar<"error">(translate("Invalid quantity"));
		}
		const newValueStr = newValue.toString();
		if (newValueStr !== textboxValue) {
			setTextboxValue(newValueStr);
		}
		return newValueStr;
	};

	const onChangeTextbox: React.ChangeEventHandler<HTMLInputElement> = async (
		e,
	) => {
		const value = e.target.value;
		return Promise.resolve().then(() => {
			setTextboxValue(value);
		});
	};

	const onKeyUpTextbox: KeyboardEventHandler<HTMLInputElement> = async (e) => {
		const key = e.key;
		return Promise.resolve().then(() => {
			if (key === "Enter") {
				if (textboxValue === "") {
					setShowTextbox(false);
				}
				if (props.onChange) {
					props.onChange(+textboxValue, props.value);
				}
			}
		});
	};

	const onBlurTextbox: FocusEventHandler<HTMLInputElement> = async (e) => {
		const value = e.target.value;

		// if user didn't enter a value
		if (Number.isNaN(parseFloat(value))) {
			return;
		}

		return Promise.resolve().then(() => {
			const newValue = validateTextboxValue(value);
			if (newValue === "") {
				setShowTextbox(false);
			}
			if (props.onChange) {
				props.onChange(+newValue, props.value);
			}
		});
	};

	return (
		<div
			css={[
				css`display:flex;flex-direction:column;gap:.25rem;min-width: 50px;`,
				props.css,
			]}
		>
			<div className="flex items-center-safe justify-center-safe mt-1">
				{!showTextbox ? (
					<DropdownMenu>
						<DropdownMenuTrigger
							id={fieldId}
							className="border-box border-input hover:bg-muted focus-visible:border-ring focus-visible:ring-ring/50 w-26 rounded-lg border px-3 text-sm outline-none transition-colors focus-visible:ring-[3px] flex items-center justify-between"
							style={{ height: inputHeight }}
						>
							<span className="truncate text-center flex-1">{`${labelPrefix}${props.value}`}</span>
							<ChevronDown className="size-4 shrink-0" />
						</DropdownMenuTrigger>
						<DropdownMenuContent
							align="end"
							className="max-h-56 overflow-y-auto"
						>
							{options.map((option) => (
								<DropdownMenuItem
									key={option.value}
									onClick={() => {
										void onChangeSelect(option.value);
									}}
								>
									{option.label}
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				) : (
					<Input
						id={fieldId}
						type="number"
						css={css`height:${inputHeight};text-align:center;`}
						className="text-center text-xs w-26"
						value={textboxValue === "0" ? "" : textboxValue}
						onKeyUp={onKeyUpTextbox}
						onBlur={onBlurTextbox}
						onChange={onChangeTextbox}
						autoFocus={true}
					/>
				)}
			</div>
		</div>
	);
};
